import { defineMessages } from 'react-intl';
import {
  ALL_SERVICE_NAMES,
  SERVICE_CATEGORIES_NAMES,
} from '../names/index.names';
import { generalServicesIntl } from './_generalServices.intl';
import { vehiclePurchaseServicesIntl } from './_vehiclePurchaseServices.intl';
import { workshopServicesIntl } from './_workshopServices.intl';

const intlPrefix = `app.dealer.account.DealerProfileSettings.ServicesPanel.services.intl`;

/**
 * @typedef {import('../names/index.names').DealerProfileServiceCategoryKey} DealerProfileServiceCategoryKey
 */

/**
 * @typedef {import('../names/index.names').SERVICE_CATEGORIES_NAMES} ServiceCategoriesNames
 * @typedef {Record<ServiceCategoriesNames, {id: string, defaultMessage: string}>} ServiceCategoriesNamesIntl
 */

/**
 * `defineMessages` is used by `react-intl`. It is just a function that returns the object it's passed.
 * `react-intl` needs it to comply with babel-plugin-react-intl and i18n message extraction.
 *
 * @description
 * top-level filter categories
 *
 * @const profileSettingsIntl
 * @type {ServiceCategoriesNamesIntl}
 */
export const profileSettingsIntl = defineMessages({
  [SERVICE_CATEGORIES_NAMES.GENERAL_SERVICES]: {
    id: `${intlPrefix}.index.generalServices`,
    defaultMessage: 'Allgemeine Services',
  },
  [SERVICE_CATEGORIES_NAMES.VEHICLE_PURCHASE_SERVICES]: {
    id: `${intlPrefix}.index.vehiclePurchaseServices`,
    defaultMessage: 'Fahrzeugkauf',
  },
  [SERVICE_CATEGORIES_NAMES.WORKSHOP_SERVICES]: {
    id: `${intlPrefix}.index.workshopServices`,
    defaultMessage: 'Werkstattleistungen',
  },
});

/**
 * @typedef {import('./_generalServices.intl').GeneralServicesNamesIntl} GeneralServicesNamesIntl
 * @typedef {import('./_vehiclePurchaseServices.intl').VehiclePurchaseServicesNamesIntl} VehiclePurchaseServicesNamesIntl
 * @typedef {import('./_workshopServices.intl').WorkshopServicesNamesIntl} WorkshopServicesNamesIntl
 */
/**
 * @const allServicesIntl
 * @type {Record<DealerProfileServiceCategoryKey, GeneralServicesNamesIntl|VehiclePurchaseServicesNamesIntl|WorkshopServicesNamesIntl>}
 *
 * @description
 * dictionary of services by service name
 *
 */
export const allServicesIntl = {
  [SERVICE_CATEGORIES_NAMES.GENERAL_SERVICES]: generalServicesIntl,
  [SERVICE_CATEGORIES_NAMES.VEHICLE_PURCHASE_SERVICES]: vehiclePurchaseServicesIntl,
  [SERVICE_CATEGORIES_NAMES.WORKSHOP_SERVICES]: workshopServicesIntl,
};

/**
 * @typedef {import('../index').IntlType} IntlType
 *
 * @param {DealerProfileServiceCategoryKey} serviceCategory
 * @returns {(intl: IntlType) => Record<DealerProfileServiceCategoryKey, {name: DealerProfileServiceCategoryKey, label: string}>}
 *
 * @description
 *
 * creates dictionary of services with their dealer-service api name and translation label
 */
export const generateServicesIntl = serviceCategory => intl => {
  const servicesDictionary = {};
  const serviceNamesForCategory = Object.keys(
    ALL_SERVICE_NAMES[serviceCategory],
  );

  serviceNamesForCategory.forEach(serviceName => {
    servicesDictionary[serviceName] = {
      name: serviceName,
      label: intl.formatMessage(allServicesIntl[serviceCategory][serviceName]),
    };
  });

  return Object.values(servicesDictionary);
};
